import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  Autorenew,
  Cancel,
  Delete,
  ArrowUpward,
  ArrowDownward,
  Edit,
  Check,
  Close,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { TemplateSection, useTemplateBuilder } from './TemplateBuilderContext';
import { MarkdownNoteSectionDisplayBox } from '../Note/MarkdownNoteSectionDisplayBox';
import { useNavigate } from 'react-router-dom';

// Styled components
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'box-shadow 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[3],
  },
}));

// Processing overlay with blur and animation
const ProcessingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  borderRadius: theme.shape.borderRadius,
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
}));

const TemplatePreview: React.FC = () => {
  const {
    templateSections,
    selectedSectionId,
    handleSelectSection,
    templateName,
    templateDescription,
    handleSaveTemplate,
    updateSectionsAsync,
    currentPollingTaskId,
    processingSectionIds,
    sessionId,
    setTemplateSections,
    handleUpdateSection,
    setTemplateName,
    setTemplateDescription,
  } = useTemplateBuilder();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [movingSection, setMovingSection] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editedSectionName, setEditedSectionName] = useState('');
  const [editingTemplateName, setEditingTemplateName] = useState(false);
  const [editedTemplateName, setEditedTemplateName] = useState('');
  const [editingTemplateDescription, setEditingTemplateDescription] = useState(false);
  const [editedTemplateDescription, setEditedTemplateDescription] = useState('');
  const navigate = useNavigate();

  const handleCancelTemplate = () => {
    setOpenCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    // Remove localStorage key
    localStorage.removeItem('template_builder_session_id');

    // Stub API call to discard the template
    if (sessionId) {
      // This is a stub - in a real implementation we would call the API
      console.log('Discarding template session:', sessionId);
      // Actual API call would be something like:
      // await APIService.makeAPIPostRequest({
      //   requestString: "/templatebuilder/discardTemplateBuilderSession",
      //   body: { session_id: sessionId },
      //   accessToken: await getAccessToken(),
      // });
    }

    // Close dialog
    setOpenCancelDialog(false);

    // Navigate back to templates page
    navigate('/templates');
  };

  const handleDeleteSection = (sectionId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent selection of the section when deleting

    // Find the sections to keep
    const updatedSections = templateSections.filter(section => section.module_id !== sectionId);

    // Update the context with the new sections
    setTemplateSections(updatedSections);

    // If the deleted section was selected, select the first available section
    if (selectedSectionId === sectionId && updatedSections.length > 0) {
      handleSelectSection(updatedSections[0].module_id);
    }
  };

  const handleMoveSection = (
    sectionId: string,
    direction: 'up' | 'down',
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent selection of the section when moving

    const sectionIndex = templateSections.findIndex(section => section.module_id === sectionId);

    if (sectionIndex === -1) return;

    const newIndex =
      direction === 'up'
        ? Math.max(0, sectionIndex - 1)
        : Math.min(templateSections.length - 1, sectionIndex + 1);

    // If already at the boundary, do nothing
    if (newIndex === sectionIndex) return;

    // Set the moving section to trigger animation
    setMovingSection(sectionId);

    // Create a new array with the reordered sections
    const updatedSections = [...templateSections];
    const [movedSection] = updatedSections.splice(sectionIndex, 1);
    updatedSections.splice(newIndex, 0, movedSection);

    // Update the context with the new sections
    setTemplateSections(updatedSections);

    // Clear the moving section after animation completes
    setTimeout(() => {
      setMovingSection(null);
    }, 500); // Animation duration is 500ms
  };

  // Handle section name edit mode
  const handleStartEditing = (sectionId: string, currentName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingSectionId(sectionId);
    setEditedSectionName(currentName);
  };

  const handleCancelEditing = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingSectionId(null);
  };

  // Save the edited section name
  const handleSaveEditing = () => {
    if (editingSectionId) {
      const sectionToUpdate = templateSections.find(
        section => section.module_id === editingSectionId,
      );

      if (sectionToUpdate) {
        handleUpdateSection({
          ...sectionToUpdate,
          public_name: editedSectionName,
        });
      }
      setEditingSectionId(null);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveEditing();
    } else if (event.key === 'Escape') {
      setEditingSectionId(null);
    }
  };

  // Handle blur event
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Save on blur
    if (editingSectionId) {
      handleSaveEditing();
    }
  };

  // Handle template name edit mode
  const handleStartEditingTemplateName = () => {
    setEditingTemplateName(true);
    setEditedTemplateName(templateName || '');
  };

  const handleSaveTemplateName = () => {
    setTemplateName(editedTemplateName);
    setEditingTemplateName(false);
  };

  const handleCancelEditingTemplateName = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingTemplateName(false);
  };

  // Handle template description edit mode
  const handleStartEditingTemplateDescription = () => {
    setEditingTemplateDescription(true);
    setEditedTemplateDescription(templateDescription || '');
  };

  const handleSaveTemplateDescription = () => {
    setTemplateDescription(editedTemplateDescription);
    setEditingTemplateDescription(false);
  };

  const handleCancelEditingTemplateDescription = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingTemplateDescription(false);
  };

  // Handle keyboard events for template name
  const handleTemplateNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveTemplateName();
    } else if (event.key === 'Escape') {
      setEditingTemplateName(false);
    }
  };

  // Handle keyboard events for template description
  const handleTemplateDescriptionKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveTemplateDescription();
    } else if (event.key === 'Escape') {
      setEditingTemplateDescription(false);
    }
  };

  return (
    <Paper
      id="legacy-template-preview"
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        {editingTemplateName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField
              value={editedTemplateName}
              onChange={e => setEditedTemplateName(e.target.value)}
              variant="standard"
              fullWidth
              autoFocus
              onKeyDown={handleTemplateNameKeyDown}
              onBlur={handleSaveTemplateName}
              sx={{ mr: 1 }}
             slotProps={{ input: {
                sx: {
                  fontSize: '1.25rem',
                  fontWeight: 500,
                },
                endAdornment: (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={handleSaveTemplateName}
                      sx={{ color: 'primary.main' }}
                    >
                      <Check fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditingTemplateName}
                      sx={{ color: 'action.active' }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ),
              } }}/>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5">{templateName || 'Template Preview'}</Typography>
            <IconButton
              size="small"
              onClick={handleStartEditingTemplateName}
              sx={{
                ml: 1,
                color: 'primary.main',
                opacity: 0.5,
                '&:hover': { opacity: 1 },
              }}
              disabled={!!currentPollingTaskId}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {editingTemplateDescription ? (
        <Box sx={{ mb: 3 }}>
          <TextField
            value={editedTemplateDescription}
            onChange={e => setEditedTemplateDescription(e.target.value)}
            variant="standard"
            fullWidth
            multiline
            rows={2}
            autoFocus
            onKeyDown={handleTemplateDescriptionKeyDown}
            onBlur={handleSaveTemplateDescription}
            sx={{ mr: 1 }}
           slotProps={{ input: {
              sx: {
                fontSize: '0.875rem',
              },
              endAdornment: (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={handleSaveTemplateDescription}
                    sx={{ color: 'primary.main' }}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancelEditingTemplateDescription}
                    sx={{ color: 'action.active' }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ),
            } }}/>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {templateDescription ||
              'This is how your template will appear based on the selected transcript. Click on any section to customize it.'}
          </Typography>
          <IconButton
            size="small"
            onClick={handleStartEditingTemplateDescription}
            sx={{
              ml: 1,
              color: 'primary.main',
              opacity: 0.5,
              '&:hover': { opacity: 1 },
            }}
            disabled={!!currentPollingTaskId}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <Box
        id="legacy-template-sections"
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}
      >
        <List sx={{ width: '100%' }} disablePadding>
          {templateSections.map((section, index) => {
            const isProcessing = processingSectionIds.includes(section.module_id);
            const isMoving = movingSection === section.module_id;
            const isEditing = editingSectionId === section.module_id;

            return (
              <ListItem
                key={section.module_id}
                disablePadding
                sx={{
                  mb: 2,
                  transition: 'transform 0.5s ease',
                  transform: isMoving ? 'scale(0.98)' : 'scale(1)',
                }}
              >
                <ListItemButton
                  selected={selectedSectionId === section.module_id}
                  onClick={() => handleSelectSection(section.module_id)}
                  sx={{
                    display: 'block',
                    p: 0,
                    backgroundColor:
                      selectedSectionId === section.module_id
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'transparent',
                    borderRadius: 1,
                  }}
                  disabled={isProcessing}
                >
                  <SectionPaper elevation={0} sx={{ position: 'relative', minHeight: '150px' }}>
                    {/* Processing overlay with animation */}
                    <ProcessingOverlay
                      sx={{
                        opacity: isProcessing ? 1 : 0,
                        pointerEvents: isProcessing ? 'auto' : 'none',
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Autorenew
                          sx={{
                            fontSize: 40,
                            color: 'primary.main',
                            animation: 'spin 2s linear infinite',
                            mb: 1,
                          }}
                        />
                        <Chip label="Updating..." size="small" color="primary" variant="outlined" />
                      </Box>
                    </ProcessingOverlay>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                        transition: 'opacity 0.3s ease-in-out',
                        opacity: isProcessing ? 0.5 : 1,
                      }}
                    >
                      {isEditing ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <TextField
                            value={editedSectionName}
                            onChange={e => setEditedSectionName(e.target.value)}
                            variant="standard"
                            fullWidth
                            autoFocus
                            onClick={e => e.stopPropagation()}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                            sx={{ mr: 1 }}
                           slotProps={{ input: {
                              sx: {
                                fontSize: '1.25rem',
                                fontWeight: 500,
                              },
                              endAdornment: (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={handleSaveEditing}
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <Check fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEditing}
                                    sx={{ color: 'action.active' }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Box>
                              ),
                            } }}/>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="h6" sx={{ fontWeight: selectedSectionId === section.module_id ? 600 : 400 }}
                          >
                            {section.public_name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={e =>
                              handleStartEditing(section.module_id, section.public_name, e)
                            }
                            sx={{
                              ml: 1,
                              color: 'primary.main',
                              opacity: 0.5,
                              '&:hover': { opacity: 1 },
                            }}
                            disabled={isProcessing || !!currentPollingTaskId}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      )}

                      {/* Section controls */}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Up Arrow */}
                        <Tooltip title="Move up">
                          <IconButton
                            size="small"
                            onClick={e => handleMoveSection(section.module_id, 'up', e)}
                            disabled={index === 0 || isProcessing || !!currentPollingTaskId}
                            sx={{
                              color: 'action.active',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Down Arrow */}
                        <Tooltip title="Move down">
                          <IconButton
                            size="small"
                            onClick={e => handleMoveSection(section.module_id, 'down', e)}
                            disabled={
                              index === templateSections.length - 1 ||
                              isProcessing ||
                              !!currentPollingTaskId
                            }
                            sx={{
                              color: 'action.active',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Delete Button */}
                        <Tooltip title="Delete section">
                          <IconButton
                            size="small"
                            onClick={e => handleDeleteSection(section.module_id, e)}
                            disabled={isProcessing || !!currentPollingTaskId}
                            sx={{
                              color: 'action.active',
                              '&:hover': { color: 'error.main' },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        transition: 'opacity 0.3s ease-in-out',
                        opacity: isProcessing ? 0.5 : 1,
                      }}
                    >
                      {section.public_description}
                    </Typography>

                    <Box
                      sx={{
                        transition: 'opacity 0.3s ease-in-out',
                        opacity: isProcessing ? 0.5 : 1,
                      }}
                    >
                      {section.current_content ? (
                        <MarkdownNoteSectionDisplayBox text={section.current_content} />
                      ) : (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          <em>No content generated yet</em>
                        </Box>
                      )}
                    </Box>
                  </SectionPaper>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle>Discard Template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to discard this template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>No, Keep Editing</Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus>
            Yes, Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// Add keyframe animation for the spinning icon
const styleElement = document.createElement('style');
styleElement.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleElement);

export default TemplatePreview;
